import { useQuery, useMutation, useQueryClient, type QueryKey } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/auth'
import { teamsKey } from './useTeam'
import { teamMembersKey } from './useTeamMembers'
import { toAppError, type AppError } from '../lib/errors'

/**
 * Team-discovery hooks — pending invitations addressed to me, teams my
 * email domain can discover, and the request-to-join mutation.
 *
 * Backs the Shell / Settings empty-state card for users who aren't in a
 * shared team yet. The server-side RPCs (2026-05-14-team-discovery.sql)
 * enforce:
 *   - single-shared-team invariant (can only discover/join if not already
 *     in a shared team)
 *   - domain proof-of-ownership (auth.email() domain must match)
 *   - webmail block list (gmail/outlook/etc can't be claimed)
 *
 * Types are hand-written — the new RPCs aren't in `database.types.ts`
 * yet (types are regenerated post-merge per the team's convention), so
 * each call site casts `supabase.rpc` via `unknown` to access the
 * untyped form.
 */

// Local alias so the unknown-cast is expressed once per hook.
type UntypedRpc = {
  rpc: (
    fn: string,
    args?: Record<string, unknown>,
  ) => Promise<{ data: unknown; error: { message: string } | null }>
}

const rpc = (): UntypedRpc => supabase as unknown as UntypedRpc

// ──────────────────────────────────────────────────────────────────────────
// Types — match the RPC signatures in 2026-05-14-team-discovery.sql
// ──────────────────────────────────────────────────────────────────────────

export interface PendingTeamInvitation {
  invitation_id: string
  token: string
  team_id: string
  team_name: string
  invited_by_email: string | null
  invited_by_name: string | null
  expires_at: string
  created_at: string
}

export interface JoinableTeam {
  team_id: string
  team_name: string
  member_count: number
  // Post-2026-05-16: the per-domain `mode` column was collapsed into a
  // single team-level boolean. `true` = one-click join, `false` = owner
  // must admit.
  auto_join_by_domain: boolean
  has_pending_request: boolean
}

export interface JoinRequestRow {
  id: string
  team_id: string
  user_id: string
  requested_at: string
  decided_at: string | null
  decision: 'pending' | 'approved' | 'declined' | 'expired'
  decided_by: string | null
  expires_at: string
}

export const pendingTeamInvitationsKey = (userId: string | undefined): QueryKey =>
  ['pendingTeamInvitations', userId ?? 'anon']
export const joinableTeamsKey = (userId: string | undefined): QueryKey =>
  ['joinableTeams', userId ?? 'anon']

// ──────────────────────────────────────────────────────────────────────────
// get_my_pending_invitations — invites addressed to my auth.email()
// ──────────────────────────────────────────────────────────────────────────

/**
 * Named `usePendingTeamInvitations` rather than `usePendingInvitations` —
 * the latter is already used by the LinkedIn sent-invitations hook. Two
 * unrelated domains shouldn't collide on symbol name.
 */
export function usePendingTeamInvitations() {
  const { user } = useAuth()
  const key = pendingTeamInvitationsKey(user?.id)
  const query = useQuery<PendingTeamInvitation[]>({
    queryKey: key,
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await rpc().rpc('get_my_pending_invitations')
      if (error) throw new Error(error.message)
      return (data as PendingTeamInvitation[] | null) ?? []
    },
  })
  return {
    invitations: query.data ?? [],
    loading: !!user && query.isPending,
    error: query.error ? toAppError(query.error) : null as AppError | null,
  }
}

// ──────────────────────────────────────────────────────────────────────────
// find_joinable_teams_for_me — teams matching my email domain
// ──────────────────────────────────────────────────────────────────────────

export function useJoinableTeams() {
  const { user } = useAuth()
  const key = joinableTeamsKey(user?.id)
  const query = useQuery<JoinableTeam[]>({
    queryKey: key,
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await rpc().rpc('find_joinable_teams_for_me')
      if (error) throw new Error(error.message)
      return (data as JoinableTeam[] | null) ?? []
    },
  })
  return {
    teams: query.data ?? [],
    loading: !!user && query.isPending,
    error: query.error ? toAppError(query.error) : null as AppError | null,
  }
}

// ──────────────────────────────────────────────────────────────────────────
// request_to_join(team_id) — auto-join or create pending request
// ──────────────────────────────────────────────────────────────────────────

/**
 * Calls `request_to_join`. For teams with `auto_join_by_domain=true` the RPC:
 *   - inserts a team_members row for the caller
 *   - flips profiles.current_team_id to the target team
 *   - returns a team_join_requests row with decision='approved'
 *
 * The "X joined" email is fired server-side inside the RPC itself (see
 * 2026-05-17-followup-hardening.sql §2). The client no longer invokes
 * `send-team-join-notification` directly — that surface was a replay
 * vector (any existing team member could re-trigger emails), so the edge
 * function is now internal-secret only.
 */
export function useRequestToJoin() {
  const { user } = useAuth()
  const qc = useQueryClient()
  return useMutation<JoinRequestRow, Error, string>({
    mutationKey: ['teamDiscovery', 'requestToJoin'],
    mutationFn: async (teamId: string) => {
      if (!user) throw new Error('Not authenticated')
      const { data, error } = await rpc().rpc('request_to_join', { p_team_id: teamId })
      if (error) throw new Error(error.message)
      const row = data as JoinRequestRow
      return row
    },
    onSuccess: (row) => {
      // Pending-request flag on the discoverable path, plus the full
      // team-membership refresh on the auto-join path. We invalidate
      // broadly — these are low-frequency mutations and the refetch is
      // the thing that makes the empty-state UI collapse into the
      // real team UI.
      qc.invalidateQueries({ queryKey: joinableTeamsKey(user?.id) })
      if (row.decision === 'approved') {
        qc.invalidateQueries({ queryKey: teamsKey(user?.id) })
        qc.invalidateQueries({ queryKey: teamMembersKey(row.team_id) })
        qc.invalidateQueries({ queryKey: ['profile', user?.id] })
        qc.invalidateQueries({ queryKey: ['templates'] })
      }
    },
  })
}
